import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MNTableContent, TableContentData, TableDataVersion } from '../../../../common/table/TableTypes';
import { asObject } from '../../../../common/utils/immutableHelper';
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import {
    convertTableContentDataToV2,
    convertTableContentDataToV3,
    normalizeTableContentDataDraftContent,
} from './hotTableDataConversionUtils';
import logger from '../../../logger';
import { convertReduxValueToHotValue } from '../utils/dataUtils/persistence/tablePersistenceValueUtils';
import { userLanguagePreferenceSelector } from '../../../user/currentUserSelector';
import {
    getElementId,
    getTableContent,
    getTableContentData,
    getTableContentVersion,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

type VersionedTableContent = {
    data: TableContentData;
    version: TableDataVersion.V2 | TableDataVersion.V3;
};

const getTableContentDataAtLeastV2 = (tableContent: MNTableContent, elementId?: string): VersionedTableContent => {
    // If table is already V2 or V3, return existing data with normalization applied
    if (tableContent.version === TableDataVersion.V2 || tableContent.version === TableDataVersion.V3)
        return {
            data: normalizeTableContentDataDraftContent(tableContent.data),
            version: tableContent.version,
        };

    try {
        // Now we need to convert from V1 to V2
        const tableContentDataV2 = convertTableContentDataToV2(tableContent.data);
        return {
            data: normalizeTableContentDataDraftContent(tableContentDataV2),
            version: TableDataVersion.V2,
        };
    } catch (e) {
        logger.error('Failed to convert table content data to Version 2', e, { elementId });

        // If fail to convert to V2, let it be caught by the element error boundary
        throw e;
    }
};

const getTableContentDataV3 = (
    tableContent: VersionedTableContent,
    isTableDataV3Enabled: boolean,
    elementId?: string,
): VersionedTableContent | undefined => {
    if (tableContent.version === TableDataVersion.V3) return tableContent;

    if (!isTableDataV3Enabled) return;

    try {
        // Now we need to convert from V2 to V3
        return {
            data: convertTableContentDataToV3(tableContent.data),
            version: TableDataVersion.V3,
        };
    } catch (e) {
        logger.error('Failed to convert table content data to Version 3', e, { elementId });
        return;
    }
};

export const getVersionedTableContent = (
    tableContent: MNTableContent,
    isTableDataV3Enabled: boolean,
    elementId?: string,
): VersionedTableContent => {
    const tableContentAtLeastV2 = getTableContentDataAtLeastV2(tableContent, elementId);

    return getTableContentDataV3(tableContentAtLeastV2, isTableDataV3Enabled, elementId) || tableContentAtLeastV2;
};

/**
 * Gets the table content data in V2 or V3 format, depending on the enabled feature flags and the source data version.
 *
 * This will convert:
 * - V1 data to V2 if `tableRearchV2` experiment is on
 * - V1/V2 data to V3 if both `tableRearchV2` and `tableTiptapV3` experiments are on
 */
export const useHotTableVersionedData = (element: ImMNElement): VersionedTableContent => {
    const locale = useSelector(userLanguagePreferenceSelector);

    const isTableDataV3Enabled = useSelector(getIsFeatureEnabledForCurrentUser(ExperimentId.tableTiptapV3));

    const elementId = getElementId(element);
    const imTableContentData = getTableContentData(element);
    const tableDataVersion = getTableContentVersion(element);

    // Convert the table content data from the redux format to the HoT Table format
    return useMemo(() => {
        const tableContent = asObject<MNTableContent>(getTableContent(element));

        const versionedTableContent = getVersionedTableContent(tableContent, isTableDataV3Enabled, elementId);

        return {
            // TODO - WEB-14572 - This might be unnecessary if we store both formats or render to the locale at the cell level
            data: versionedTableContent.data.map((rows) =>
                rows.map((cell) => convertReduxValueToHotValue(cell, locale)),
            ),
            version: versionedTableContent.version,
        };
    }, [imTableContentData, tableDataVersion, isTableDataV3Enabled, locale]);
};
